import { useState, useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import styled from '@emotion/styled'
import { CheckoutConfigGratuityAmount, Money } from '@open-tender/types'
import { formatDollars, formatPercentage } from '@open-tender/utils'
import {
  AppState,
  selectCheckout,
  selectContent,
  updateForm,
  useAppDispatch,
  useAppSelector,
  validateOrder,
} from '@open-tender/cloud'
import { Button, Checkmark, Input } from 'components'
import CheckoutSection from './CheckoutSection'
import CheckoutTipButton from './CheckoutTipButton'
import {
  resolveTipUpdate,
  shouldClearTip,
  TipUpdateResult,
} from './checkoutTipHelpers'

const CheckoutTipView = styled.div`
  margin: 1.5rem 0 0;
`

const CheckoutTipOptions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    gap: 0.5rem;
  }
`

const CheckoutTipCustom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1.5rem 0 0;
`

const CheckoutTipCustomInput = styled.div`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  padding: 0 2rem 0 0;
`
const CheckoutTipCustomCheckmark = styled.div`
  position: absolute;
  top: 0;
  right: 2rem;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const CheckoutTipCustomButton = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
`

const defaultOptions: CheckoutConfigGratuityAmount[] = [
  { amount: '10.00', percent: '10.00' },
  { amount: '15.00', percent: '15.00' },
  { amount: '18.00', percent: '18.00' },
  { amount: '20.00', percent: '20.00' },
]

const CheckoutTip = () => {
  const dispatch = useAppDispatch()
  const store = useStore<AppState>()
  const { check, form, loading } = useAppSelector(selectCheckout)
  const { checkout: config } = useAppSelector(selectContent) || {}
  const tipSettings = check?.config.gratuity
  const { has_tip, options, default: defaultOption } = tipSettings || {}
  const tipOptions = options || defaultOptions
  // While a restore is in flight, `tip` may be transiently null, so fall back
  // to the shadow amount so the custom input doesn't flash empty.
  const initialTip =
    form.tipPercent === null ? form.tip || form.tipCustomAmount || '' : ''
  const [customTip, setCustomTip] = useState(initialTip)
  const customTipApplied =
    customTip && customTip === check?.totals.tip ? true : false
  const tipUpdateInFlightRef = useRef(false)
  const isMountedRef = useRef(true)
  const hasCheck = Boolean(check)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (tipUpdateInFlightRef.current) return
    const tipSelection = {
      tip: form.tip,
      tipPercent: form.tipPercent,
      tipCustomAmount: form.tipCustomAmount,
    }

    if (hasCheck && shouldClearTip(Boolean(has_tip), tipSelection)) {
      tipUpdateInFlightRef.current = true
      void (async () => {
        try {
          await dispatch(
            updateForm({ tip: null, tipPercent: null, tipCustomAmount: null })
          )
        } finally {
          tipUpdateInFlightRef.current = false
        }
      })()
      return
    }

    if (!has_tip || loading === 'pending') return
    const result = resolveTipUpdate(
      tipSelection,
      tipOptions,
      defaultOption || null
    )
    if (!result) return

    tipUpdateInFlightRef.current = true
    void (async () => {
      try {
        // Re-check live state after every step: a dependency change skipped
        // while this ref was true would otherwise never be retried, since
        // releasing a ref does not trigger a render.
        let next: TipUpdateResult | null = result
        while (next && isMountedRef.current) {
          await dispatch(updateForm(next.update))
          if (!isMountedRef.current) break
          if (next.kind === 'recalculate' || next.kind === 'restore') {
            await dispatch(validateOrder())
          }
          if (!isMountedRef.current) break

          const latest = store.getState().checkout
          const latestSelection = {
            tip: latest.form.tip,
            tipPercent: latest.form.tipPercent,
            tipCustomAmount: latest.form.tipCustomAmount,
          }
          const gratuity = latest.check?.config.gratuity
          if (
            latest.check &&
            shouldClearTip(Boolean(gratuity?.has_tip), latestSelection)
          ) {
            await dispatch(
              updateForm({ tip: null, tipPercent: null, tipCustomAmount: null })
            )
            break
          }
          if (!gratuity?.has_tip) break
          next = resolveTipUpdate(
            latestSelection,
            gratuity.options,
            gratuity.default
          )
        }
      } finally {
        tipUpdateInFlightRef.current = false
      }
    })()
  }, [
    has_tip,
    form.tip,
    form.tipPercent,
    form.tipCustomAmount,
    tipOptions,
    defaultOption,
    loading,
    hasCheck,
    dispatch,
    store,
  ])

  useEffect(() => {
    setCustomTip(initialTip)
  }, [initialTip])

  if (!has_tip) return null

  const chooseTip = async (amount: Money, percent: Money) => {
    setCustomTip('')
    await dispatch(
      updateForm({ tip: amount, tipPercent: percent, tipCustomAmount: null })
    )
    dispatch(validateOrder())
  }

  const handleCustomTip = (txt: string) => {
    setCustomTip(txt)
  }

  const applyCustomTip = async () => {
    const formatted = parseFloat(customTip).toFixed(2) as Money
    setCustomTip(formatted)
    await dispatch(
      updateForm({
        tip: formatted,
        tipPercent: null,
        tipCustomAmount: formatted,
      })
    )
    dispatch(validateOrder())
  }

  return (
    <CheckoutSection title={config?.gratuity.title}>
      <CheckoutTipView>
        <CheckoutTipOptions>
          {tipOptions.map((i) => {
            const isApplied = !customTip && form.tipPercent === i.percent
            return (
              <CheckoutTipButton
                key={`${i.percent}-${i.amount}`}
                title={`${formatPercentage(i.percent)}%`}
                subtitle={formatDollars(i.amount)}
                onPress={() => chooseTip(i.amount, i.percent)}
                isApplied={isApplied}
                disabled={isApplied}
              />
            )
          })}
        </CheckoutTipOptions>
        <CheckoutTipCustom>
          <CheckoutTipCustomInput>
            <Input
              label="Enter custom tip"
              name="custom_tip"
              type="number"
              value={customTip}
              onChange={(evt) => handleCustomTip(evt.target.value)}
              style={{ margin: 0 }}
            />
            {customTipApplied && (
              <CheckoutTipCustomCheckmark>
                <Checkmark />
              </CheckoutTipCustomCheckmark>
            )}
          </CheckoutTipCustomInput>
          <CheckoutTipCustomButton>
            <Button
              onClick={applyCustomTip}
              disabled={customTip === '' || customTipApplied}
              size="small"
              color="secondary"
            >
              Apply
            </Button>
          </CheckoutTipCustomButton>
        </CheckoutTipCustom>
      </CheckoutTipView>
    </CheckoutSection>
  )
}

export default CheckoutTip
